import React, { forwardRef } from "react";
import { arrayOf, bool, func, object, oneOfType, string } from "prop-types";
import styled from "styled-components";
import { useTranslation } from "@lib/i18n";

import FilterResultList from "@components/TestDrive/FilterResultList";
import SmallAccordion from "@components/shared/SmallAccordion";
import TestDriveTypesList from "@components/TestDrive/TestDriveTypesList";
import CollapsableContainer from "@components/shared/CollapsableContainer";
import { desktop } from "@components/shared/utils";

import {
  addressType,
  testDriveLocationType,
  testDriveType,
  testDriveVehicleType,
} from "@types";

const StyledCollapsableContainer = styled(CollapsableContainer)`
  margin-bottom: 0;
  padding: 1rem 2rem;
  background: ${({ customColor, theme }) => customColor ?? theme.colors.white};

  ${desktop`
    display: none;
  `}
`;

const StyledSmallAccordion = styled(SmallAccordion)`
  display: none;

  ${desktop`
    display: block;
  `}
`;

const Container = styled.div`
  ${desktop`
    min-height: 415px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `}
`;

const TestDrivePointsSection = forwardRef(
  (
    {
      affiliation,
      testDrivePoints,
      address,
      selectedTestDrivePoint,
      typeFilter,
      setTypeFilter,
      accentColor,
      typesContainerRef,
      isTypeExplanationOpen,
      setIsTypeExplanationOpen,
      defaultColor,
      testDriveTypes,
    },
    ref
  ) => {
    const { t } = useTranslation();

    // Move selected test drive point to the top
    const sortedTestDrivePoints = testDrivePoints.sort((a, b) => {
      const selectedPointId = selectedTestDrivePoint?._id;

      if (a._id === selectedPointId) {
        return -1;
      }

      if (b._id === selectedPointId) {
        return 1;
      }

      return 0;
    });

    return (
      <Container ref={ref}>
        {testDrivePoints.length > 0 && (
          <FilterResultList
            activePointId={selectedTestDrivePoint?._id}
            testDrivePoints={sortedTestDrivePoints}
            testDriveTypes={testDriveTypes}
            isOpen={Boolean(address || typeFilter || selectedTestDrivePoint)}
            accentColor={accentColor}
          />
        )}

        <StyledSmallAccordion
          title={t("testDrive.try")}
          ref={typesContainerRef}
          isOpen={isTypeExplanationOpen}
          setIsOpen={setIsTypeExplanationOpen}
          customColor={defaultColor}
        >
          <TestDriveTypesList
            testDriveTypes={testDriveTypes}
            setTypeFilter={setTypeFilter}
            affiliation={affiliation}
          />
        </StyledSmallAccordion>

        <StyledCollapsableContainer
          title={t("testDrive.try")}
          ref={typesContainerRef}
          isOpen={isTypeExplanationOpen}
          onChange={isOpen => {
            setIsTypeExplanationOpen(!isOpen);
          }}
          customColor={isTypeExplanationOpen ? null : defaultColor}
        >
          <TestDriveTypesList
            testDriveTypes={testDriveTypes}
            setTypeFilter={setTypeFilter}
            affiliation={affiliation}
          />
        </StyledCollapsableContainer>
      </Container>
    );
  }
);

TestDrivePointsSection.displayName = "TestDrivePointsSection";

TestDrivePointsSection.propTypes = {
  accentColor: string.isRequired,
  defaultColor: string.isRequired,
  typesContainerRef: object.isRequired,
  isTypeExplanationOpen: bool.isRequired,
  setIsTypeExplanationOpen: func.isRequired,
  testDriveTypes: arrayOf(testDriveType).isRequired,
  address: addressType,
  affiliation: string,
  typeFilter: string,
  setTypeFilter: func,
  testDrivePoints: arrayOf(
    oneOfType([testDriveVehicleType, testDriveLocationType])
  ).isRequired,
  selectedTestDrivePoint: oneOfType([
    testDriveVehicleType,
    testDriveLocationType,
  ]),
};

export default TestDrivePointsSection;
